import { useState, useEffect } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { AbsolinDisclaimer } from "@/components/AbsolinDisclaimer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { getAssessmentQuestions, submitAssessment, getAssessmentHistory, downloadAssessmentPDF } from "@/lib/api";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend
} from "recharts";
import {
  ArrowRight, ArrowLeft, CheckCircle, Loader2, ChevronRight, Download, History, AlertCircle,
  IndianRupee, ShoppingCart, Package, Factory, TrendingUp, Users, ShieldCheck, Truck,
  BarChart3, Target, AlertTriangle, Award
} from "lucide-react";
import { SEOHead } from "@/components/SEOHead";

const DEPT_ICONS = {
  finance: IndianRupee, procurement: ShoppingCart, inventory: Package,
  production: Factory, sales: TrendingUp, hr: Users,
  quality: ShieldCheck, supply_chain: Truck
};

const DEPT_COLORS = {
  finance: "#0066CC", procurement: "#059669", inventory: "#D97706",
  production: "#7C3AED", sales: "#DC2626", hr: "#0891B2",
  quality: "#4F46E5", supply_chain: "#EA580C"
};

export default function AssessmentPage() {
  const [step, setStep] = useState("landing");
  const [departments, setDepartments] = useState([]);
  const [questions, setQuestions] = useState({});
  const [currentDeptIdx, setCurrentDeptIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [companyInfo, setCompanyInfo] = useState({
    company_name: "", industry: "", employee_count: "",
    annual_turnover: "", contact_name: "", contact_email: "", contact_phone: ""
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => { loadQuestions(); }, []);

  const loadQuestions = async () => {
    try {
      const res = await getAssessmentQuestions();
      setDepartments(res.data.departments);
      setQuestions(res.data.questions);
    } catch { toast.error("Failed to load assessment questions"); }
  };

  const loadHistory = async (email) => {
    if (!email) return;
    setHistoryLoading(true);
    try {
      const res = await getAssessmentHistory(email);
      setHistory(res.data.assessments || []);
    } catch { /* no history yet */ }
    finally { setHistoryLoading(false); }
  };

  const handleAnswer = (qId, score, optionText) => {
    const dept = departments[currentDeptIdx]?.key;
    setAnswers(prev => ({ ...prev, [`${dept}_${qId}`]: { department: dept, question_id: qId, score, option_selected: optionText } }));
  };

  const currentDeptQuestions = questions[departments[currentDeptIdx]?.key] || [];
  const currentDeptAnswered = currentDeptQuestions.every(
    q => answers[`${departments[currentDeptIdx]?.key}_${q.id}`]
  );
  const totalAnswered = Object.keys(answers).length;
  const totalQuestions = Object.values(questions).flat().length;
  const progress = totalQuestions > 0 ? Math.round((totalAnswered / totalQuestions) * 100) : 0;

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const res = await submitAssessment({
        ...companyInfo,
        employee_count: parseInt(companyInfo.employee_count) || 0,
        answers: Object.values(answers)
      });
      setResult(res.data);
      setStep("results");
      // Refresh history
      loadHistory(companyInfo.contact_email);
    } catch { toast.error("Failed to submit assessment"); }
    finally { setLoading(false); }
  };

  const downloadReport = async (reportData) => {
    const report = reportData || result;
    if (!report) return;
    try {
      toast.info("Generating PDF report...");
      const res = await downloadAssessmentPDF(report.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Assessment_${report.company_name.replace(/\s+/g, '_')}_${new Date(report.created_at).toISOString().split('T')[0]}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded!");
    } catch {
      toast.error("Failed to download PDF");
    }
    toast.success("Report downloaded!");
  };

  const radarData = result ? departments.map(d => ({
    department: d.name.split(" ")[0],
    fullName: d.name,
    "Your Score": result.department_scores[d.key] || 0,
    "Industry Benchmark": result.benchmarks[d.key] || 3.0
  })) : [];

  const maxReached = history.length >= 3;

  // LANDING
  if (step === "landing") {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <SEOHead
          title="Free Digital Maturity Assessment Tool"
          description="Evaluate your business's digital readiness across 8 departments. Get a maturity score with industry benchmarks, gap analysis, and transformation roadmap. Free for MSMEs & SMEs."
          keywords="digital maturity assessment, business digital readiness, MSME digital assessment, ERP readiness score, digital transformation assessment India, SME technology audit"
          canonicalUrl="/assessment"
          jsonLd={{
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Digital Maturity Self-Assessment",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web",
            "offers": { "@type": "Offer", "price": "0", "priceCurrency": "INR" },
            "description": "Free tool to assess your company's digital maturity across 8 departments with industry benchmarks"
          }}
        />
        <Navbar />
        <div className="pt-24 pb-16 flex-1">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-50 border border-blue-100 rounded-full text-xs text-blue-700 font-semibold uppercase tracking-wider mb-6">
                <BarChart3 className="w-3.5 h-3.5" /> Free Assessment Tool
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight" data-testid="assessment-title">
                Digital Maturity
                <span className="text-[#0066CC]"> Self-Assessment</span>
              </h1>
              <p className="text-slate-500 text-base sm:text-lg mt-4 max-w-2xl mx-auto leading-relaxed">
                Discover where your business stands on the digital transformation journey. Get a department-wise maturity score compared against industry benchmarks.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-10">
              {[
                { icon: Target, title: "8 Departments Assessed", desc: "Finance, HR, Production, Sales, Inventory, Procurement, Quality, Supply Chain" },
                { icon: BarChart3, title: "Industry Benchmarks", desc: "Compare your score against 1000+ Indian MSMEs in your industry" },
                { icon: AlertTriangle, title: "Gap Identification", desc: "See exactly which departments are lagging and need priority attention" },
                { icon: Award, title: "Actionable Roadmap", desc: "Get a recommended transformation path based on your maturity gaps" }
              ].map((f, i) => (
                <Card key={i} className="border border-slate-200 hover:border-blue-200 transition-colors">
                  <CardContent className="p-6 flex gap-4">
                    <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center shrink-0">
                      <f.icon className="w-5 h-5 text-[#0066CC]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 text-sm">{f.title}</h3>
                      <p className="text-slate-500 text-xs mt-1">{f.desc}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center">
              <Button size="lg" onClick={() => setStep("info")}
                className="bg-[#0066CC] hover:bg-[#0055aa] text-white px-8 h-12 text-sm font-semibold rounded-xl"
                data-testid="start-assessment-btn">
                Start Assessment <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <p className="text-slate-400 text-xs mt-3">Takes about 5 minutes. 24 questions across 8 departments. Maximum 3 attempts.</p>
            </div>
          </div>
        </div>
        <AbsolinDisclaimer />
        <Footer />
      </div>
    );
  }

  // COMPANY INFO
  if (step === "info") {
    const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const validatePhone = (phone) => !phone || /^(\+91[\s-]?)?[6-9]\d{4}[\s-]?\d{5}$/.test(phone.replace(/\s/g, ''));
    const canProceed = companyInfo.company_name && companyInfo.contact_name && companyInfo.contact_email && validateEmail(companyInfo.contact_email) && validatePhone(companyInfo.contact_phone) && !maxReached;

    const handleProceed = () => {
      const errors = {};
      if (!companyInfo.company_name) errors.company_name = "Required";
      if (!companyInfo.contact_name) errors.contact_name = "Required";
      if (!companyInfo.contact_email) errors.contact_email = "Required";
      else if (!validateEmail(companyInfo.contact_email)) errors.contact_email = "Enter a valid email (e.g. you@company.com)";
      if (companyInfo.contact_phone && !validatePhone(companyInfo.contact_phone)) errors.contact_phone = "Enter valid Indian mobile (e.g. +91 98765 43210)";
      setFormErrors(errors);
      if (Object.keys(errors).length > 0) { toast.error("Please fix the form errors"); return; }
      setStep("questions");
      window.scrollTo(0, 0);
    };

    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <Navbar />
        <div className="pt-24 pb-16 max-w-lg mx-auto px-4 flex-1">
          <div className="mb-8">
            <button onClick={() => setStep("landing")} className="text-sm text-slate-500 hover:text-[#0066CC] flex items-center gap-1 mb-4">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900">Tell us about your company</h2>
            <p className="text-slate-500 text-sm mt-1">This helps us compare your scores with similar businesses.</p>
          </div>
          <Card>
            <CardContent className="p-4 sm:p-6 space-y-4">
              <div>
                <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Company Name *</Label>
                <Input className={`placeholder:text-slate-300 ${formErrors.company_name ? "border-red-400" : ""}`} value={companyInfo.company_name} onChange={e => { setCompanyInfo(p => ({...p, company_name: e.target.value})); setFormErrors(p => ({...p, company_name: undefined})); }} placeholder="e.g. ABC Manufacturing Pvt Ltd" data-testid="assessment-company" />
                {formErrors.company_name && <p className="text-red-500 text-xs mt-1">{formErrors.company_name}</p>}
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Industry</Label>
                <select className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" value={companyInfo.industry} onChange={e => setCompanyInfo(p => ({...p, industry: e.target.value}))} data-testid="assessment-industry">
                  <option value="">Select Industry</option>
                  <option value="Manufacturing">Manufacturing</option>
                  <option value="Retail">Retail & Distribution</option>
                  <option value="Services">Services</option>
                  <option value="FMCG">FMCG / Consumer Goods</option>
                  <option value="Textile">Textile & Apparel</option>
                  <option value="Pharma">Pharma & Healthcare</option>
                  <option value="Auto">Automotive & Auto Parts</option>
                  <option value="Agro">Agro-Processing & Food</option>
                  <option value="Construction">Construction & Real Estate</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Employee Count</Label>
                  <Input className="placeholder:text-slate-300" type="number" value={companyInfo.employee_count} onChange={e => setCompanyInfo(p => ({...p, employee_count: e.target.value}))} placeholder="e.g. 500" data-testid="assessment-employees" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Annual Turnover</Label>
                  <select className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" value={companyInfo.annual_turnover} onChange={e => setCompanyInfo(p => ({...p, annual_turnover: e.target.value}))}>
                    <option value="">Select Range</option>
                    <option value="< 5 Cr">Below 5 Cr</option>
                    <option value="5-25 Cr">5 - 25 Cr</option>
                    <option value="25-100 Cr">25 - 100 Cr</option>
                    <option value="100-500 Cr">100 - 500 Cr</option>
                    <option value="> 500 Cr">Above 500 Cr</option>
                  </select>
                </div>
              </div>
              <hr className="my-2" />
              <div>
                <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Your Name *</Label>
                <Input className={`placeholder:text-slate-300 ${formErrors.contact_name ? "border-red-400" : ""}`} value={companyInfo.contact_name} onChange={e => { setCompanyInfo(p => ({...p, contact_name: e.target.value})); setFormErrors(p => ({...p, contact_name: undefined})); }} placeholder="e.g. Rajesh Kumar" data-testid="assessment-name" />
                {formErrors.contact_name && <p className="text-red-500 text-xs mt-1">{formErrors.contact_name}</p>}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Email *</Label>
                  <Input className={`placeholder:text-slate-300 ${formErrors.contact_email ? "border-red-400" : ""}`} type="email" value={companyInfo.contact_email}
                    onChange={e => { setCompanyInfo(p => ({...p, contact_email: e.target.value})); setFormErrors(p => ({...p, contact_email: undefined})); }}
                    onBlur={e => { if (validateEmail(e.target.value)) loadHistory(e.target.value); }}
                    placeholder="you@company.com" data-testid="assessment-email" />
                  {formErrors.contact_email && <p className="text-red-500 text-xs mt-1">{formErrors.contact_email}</p>}
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Phone</Label>
                  <Input className={`placeholder:text-slate-300 ${formErrors.contact_phone ? "border-red-400" : ""}`} value={companyInfo.contact_phone}
                    onChange={e => { setCompanyInfo(p => ({...p, contact_phone: e.target.value})); setFormErrors(p => ({...p, contact_phone: undefined})); }}
                    placeholder="+91 98765 43210" data-testid="assessment-phone" />
                  {formErrors.contact_phone && <p className="text-red-500 text-xs mt-1">{formErrors.contact_phone}</p>}
                </div>
              </div>

              {/* History / Previous Reports */}
              {historyLoading && <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Checking previous assessments...</div>}
              {history.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 space-y-2">
                  <div className="flex items-center gap-2 text-sm font-semibold text-blue-800">
                    <History className="w-4 h-4" /> Previous Assessments ({history.length}/3)
                  </div>
                  {history.map((h, i) => (
                    <div key={h.id} className="flex items-center justify-between bg-white rounded-lg p-2.5 sm:p-3 border border-blue-100 gap-2">
                      <div className="min-w-0">
                        <p className="text-xs sm:text-sm font-medium text-slate-800 truncate">{h.company_name} — Score: {h.overall_score}/5</p>
                        <p className="text-[10px] sm:text-xs text-slate-500">{new Date(h.created_at).toLocaleDateString()} | {h.maturity_level}</p>
                      </div>
                      <Button variant="outline" size="sm" className="shrink-0 text-xs" onClick={() => downloadReport(h)} data-testid={`download-report-${i}`}>
                        <Download className="w-3 h-3 mr-1" /> PDF
                      </Button>
                    </div>
                  ))}
                  {maxReached && (
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-amber-700 bg-amber-50 p-2 rounded">
                      <AlertCircle className="w-4 h-4 shrink-0" /> Maximum 3 assessments reached.
                    </div>
                  )}
                </div>
              )}

              <Button className="w-full bg-[#0066CC] hover:bg-[#0055aa] h-11"
                disabled={maxReached} onClick={handleProceed} data-testid="assessment-proceed-btn">
                {maxReached ? "Maximum Attempts Reached" : "Proceed to Assessment"} {!maxReached && <ArrowRight className="w-4 h-4 ml-2" />}
              </Button>
            </CardContent>
          </Card>
        </div>
        <AbsolinDisclaimer />
      </div>
    );
  }

  // QUESTIONS
  if (step === "questions") {
    const dept = departments[currentDeptIdx];
    const Icon = DEPT_ICONS[dept?.key] || BarChart3;
    const isLast = currentDeptIdx === departments.length - 1;

    const goNextDept = () => {
      setCurrentDeptIdx(p => p + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    const goPrevDept = () => {
      setCurrentDeptIdx(p => p - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-24 pb-24 sm:pb-16 max-w-3xl mx-auto px-4">
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs text-slate-500 font-medium">{totalAnswered} of {totalQuestions} questions answered</span>
              <span className="text-xs font-bold text-[#0066CC]">{progress}%</span>
            </div>
            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-[#0066CC] rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
          </div>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${DEPT_COLORS[dept?.key]}15` }}>
              <Icon className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: DEPT_COLORS[dept?.key] }} />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-slate-900">{dept?.name}</h2>
              <p className="text-xs text-slate-500">Department {currentDeptIdx + 1} of {departments.length}</p>
            </div>
          </div>
          <div className="flex gap-1 mb-6 overflow-x-auto pb-2 -mx-1 px-1">
            {departments.map((d, i) => {
              const deptQs = questions[d.key] || [];
              const allAnswered = deptQs.every(q => answers[`${d.key}_${q.id}`]);
              return (
                <button key={d.key} onClick={() => { setCurrentDeptIdx(i); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                    i === currentDeptIdx ? "bg-[#0066CC] text-white" : allAnswered ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                  }`}>{d.name.split(" ")[0]}{allAnswered && i !== currentDeptIdx && <CheckCircle className="w-3 h-3 inline ml-1" />}</button>
              );
            })}
          </div>
          <div className="space-y-4 sm:space-y-5">
            {currentDeptQuestions.map((q, qi) => {
              const selectedAnswer = answers[`${dept.key}_${q.id}`];
              return (
                <Card key={q.id} className="border border-slate-200">
                  <CardContent className="p-4 sm:p-5">
                    <p className="text-sm font-semibold text-slate-800 mb-3"><span className="text-[#0066CC] mr-2">Q{qi + 1}.</span>{q.question_text}</p>
                    <div className="space-y-2">
                      {q.options.map((opt, oi) => (
                        <button key={oi} onClick={() => handleAnswer(q.id, opt.score, opt.text)}
                          data-testid={`q-${q.id}-opt-${oi}`}
                          className={`w-full text-left px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg border text-sm transition-all flex items-start gap-2.5 ${
                            selectedAnswer?.score === opt.score ? "border-[#0066CC] bg-blue-50 text-[#0066CC] font-medium" : "border-slate-200 hover:border-slate-300 text-slate-700 hover:bg-slate-50"
                          }`}>
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full border text-xs font-bold shrink-0 mt-0.5"
                            style={selectedAnswer?.score === opt.score ? { borderColor: '#0066CC', color: '#0066CC', backgroundColor: '#EBF5FF' } : { borderColor: '#CBD5E1', color: '#94A3B8' }}>{opt.score}</span>
                          <span>{opt.text}</span>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <div className="flex justify-between mt-8 gap-3">
            <Button variant="outline" className="text-sm" disabled={currentDeptIdx === 0} onClick={goPrevDept}>
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Previous
            </Button>
            {isLast ? (
              <Button className="bg-[#0066CC] hover:bg-[#0055aa] text-sm" disabled={totalAnswered < totalQuestions || loading} onClick={handleSubmit} data-testid="submit-assessment-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Get My Results <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button className="bg-[#0066CC] hover:bg-[#0055aa] text-sm" disabled={!currentDeptAnswered} onClick={goNextDept}>
                Next Dept <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // RESULTS
  if (step === "results" && result) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <Navbar />
        <div className="pt-24 pb-16 max-w-5xl mx-auto px-4 flex-1">
          <div className="text-center mb-8 sm:mb-10">
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" data-testid="assessment-results-title">Your Digital Maturity Report</h1>
            <p className="text-slate-500 mt-2 text-sm sm:text-base">{result.company_name} — {result.industry || "General"}</p>
          </div>
          <Card className="mb-6 sm:mb-8 border-2 border-[#0066CC]/20 bg-gradient-to-br from-blue-50 to-white">
            <CardContent className="p-5 sm:p-8 text-center">
              <div className="text-5xl sm:text-6xl font-extrabold text-[#0066CC]" data-testid="overall-score">{result.overall_score}</div>
              <div className="text-slate-400 text-xs sm:text-sm mt-1">out of 5.0</div>
              <div className="mt-3 inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-white border border-blue-200 rounded-full max-w-full">
                <Award className="w-4 h-4 text-[#0066CC] shrink-0" />
                <span className="text-xs sm:text-sm font-semibold text-slate-800 truncate">{result.maturity_level}</span>
              </div>
              <div className="mt-4">
                <Button variant="outline" className="text-sm" onClick={() => downloadReport()} data-testid="download-assessment-report">
                  <Download className="w-4 h-4 mr-2" /> Download Report
                </Button>
              </div>
            </CardContent>
          </Card>
          <Card className="mb-6 sm:mb-8">
            <CardContent className="p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-4">Department-wise Comparison</h3>
              <div className="h-[280px] sm:h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="#e2e8f0" />
                    <PolarAngleAxis dataKey="department" tick={{ fontSize: 10, fill: '#64748b' }} />
                    <PolarRadiusAxis angle={30} domain={[0, 5]} tick={{ fontSize: 9 }} />
                    <Radar name="Your Score" dataKey="Your Score" stroke="#0066CC" fill="#0066CC" fillOpacity={0.2} strokeWidth={2} />
                    <Radar name="Industry Benchmark" dataKey="Industry Benchmark" stroke="#10B981" fill="#10B981" fillOpacity={0.1} strokeWidth={2} strokeDasharray="5 5" />
                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
          <div className="grid sm:grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
            {departments.map(d => {
              const score = result.department_scores[d.key] || 0;
              const benchmark = result.benchmarks[d.key] || 3.0;
              const gap = benchmark - score;
              const DIcon = DEPT_ICONS[d.key] || BarChart3;
              const color = DEPT_COLORS[d.key];
              return (
                <Card key={d.key} className="border border-slate-200">
                  <CardContent className="p-4 sm:p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2 min-w-0"><DIcon className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" style={{ color }} /><span className="text-xs sm:text-sm font-semibold text-slate-800 truncate">{d.name}</span></div>
                      <span className="text-base sm:text-lg font-bold shrink-0 ml-2" style={{ color }}>{score}</span>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${(score/5)*100}%`, backgroundColor: color }} /></div>
                      <span className="text-[10px] sm:text-xs text-slate-400 w-8">/5.0</span>
                    </div>
                    <div className="flex flex-wrap justify-between text-[10px] sm:text-xs gap-1">
                      <span className="text-slate-500">Benchmark: {benchmark}</span>
                      {gap > 0 ? (
                        <span className={`font-medium ${gap >= 1.5 ? "text-red-600" : gap >= 0.8 ? "text-amber-600" : "text-green-600"}`}>
                          Gap: {gap.toFixed(1)} {gap >= 1.5 ? "(Critical)" : gap >= 0.8 ? "(Attention)" : "(On track)"}
                        </span>
                      ) : <span className="text-green-600 font-medium">Above benchmark</span>}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          {result.recommendations?.length > 0 && (
            <Card className="mb-6 sm:mb-8">
              <CardContent className="p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-4">Priority Recommendations</h3>
                <div className="space-y-2 sm:space-y-3">
                  {result.recommendations.map((r, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-2 sm:gap-4 p-2.5 sm:p-3 rounded-lg bg-slate-50 border border-slate-100">
                      <span className={`px-2 py-0.5 rounded text-[10px] sm:text-xs font-bold ${r.priority === "High" ? "bg-red-100 text-red-700" : r.priority === "Medium" ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>{r.priority}</span>
                      <span className="text-xs sm:text-sm text-slate-700 font-medium capitalize">{r.department.replace("_", " ")}</span>
                      <span className="text-[10px] sm:text-xs text-slate-500 sm:ml-auto">{r.score} → {r.benchmark}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-8">
            <Button variant="outline" className="text-sm" onClick={() => { setStep("landing"); setResult(null); setAnswers({}); }} data-testid="retake-assessment-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Take Another Assessment
            </Button>
          </div>
        </div>
        <AbsolinDisclaimer />
        <Footer />
      </div>
    );
  }

  return null;
}
